import React, { useEffect } from 'react'
import { useInvoiceActions } from '@/v2/hooks/useInvoiceActions'
import Grid from '@mui/material/Grid'
import ActionButton from '@/v2/components/common/ActionButton'
import Box from '@mui/material/Box'
import Typography from '@/v2/components/common/Typography'
import SelectPaymentCardField from '@/v2/components/common/SelectPaymentCardField'
import SelectPaymentAccountField from '@/v2/components/common/SelectPaymentAccountField'
import ControlRadioField from '@/v2/components/common/ControlRadioField'
import CurrencyInputField from '@/v2/components/common/CurrencyInputField'
import InputField from '@/v2/components/common/InputField'
import Divider from '@/v2/components/common/Divider'
import { useForm } from 'react-hook-form'
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import { fromCurrencyToNumber, toCurrency } from '@/v2/utils/helpers'
import { useSnackbarContext } from '@/v2/contexts/SnackbarContext'
import FormControl from '@mui/material/FormControl'
import Label from '@/v2/components/common/FieldLabel'
import FormErrorMessage from '@/v2/components/common/FormErrorMessage'

const ChangePaymentMethodForm = ({
  invoice,
  onClose,
  patientId,
  creditCards,
  bankAccounts,
  type,
  isInstallPayment,
}) => {
  const { onOpenSnackbar } = useSnackbarContext()
  const { changePaymentMethod, chargeInvoice } = useInvoiceActions({
    patientId,
  })

  const invoicePaymentMethodId = invoice.paymentMethodId || ''

  const hasPaymentMethods = creditCards?.length > 0

  const hasBankAccounts = bankAccounts?.length > 0

  const hasOnlyOnePaymentMethod = creditCards?.length === 1

  const paymentMethod = () => {
    if (hasOnlyOnePaymentMethod) {
      return 'credit_card'
    }

    if (hasPaymentMethods) {
      return 'credit_card'
    }

    if (hasBankAccounts) {
      return 'ach'
    }

    return 'cash'
  }

  const paymentMethodId =
    hasOnlyOnePaymentMethod && !invoicePaymentMethodId
      ? creditCards[0].id
      : invoicePaymentMethodId

  const schema = yup
    .object({
      paymentMethodId: yup.string().notRequired(),
      paymentMethod: yup
        .mixed()
        .oneOf(['cash', 'credit_card', 'check', 'ach'])
        .notRequired(),
      status: yup.string().required(),
      additionalInfo: yup.string().nullable(),
      amountTargeted: yup
        .number()
        .transform((value, originalValue) => {
          if (
            typeof originalValue === 'string' &&
            originalValue.trim() === ''
          ) {
            return undefined
          }
          return fromCurrencyToNumber(originalValue)
        })
        .notRequired(),
    })
    // Test 1: For status 'chargeAndPay', amountTargeted must be > 0.5.
    .test('amount-gt-0.5', 'Amount must be greater than $0.50', function(
      value
    ) {
      const { status, amountTargeted } = value
      if (status === 'chargeAndPay') {
        if (amountTargeted === undefined || amountTargeted <= 0.5) {
          return this.createError({
            path: 'amountTargeted',
            message: 'Amount must be greater than $0.50',
          })
        }
      }
      return true
    })
    // Test 2: For status 'change', paymentMethodId is required.
    .test(
      'paymentMethodId-required-change',
      'Payment method is required',
      function(value) {
        const { status, paymentMethodId } = value
        if (status === 'change' && !paymentMethodId) {
          return this.createError({
            path: 'paymentMethodId',
            message: 'Payment method is required',
          })
        }
        return true
      }
    )
    // Test 3: For status 'chargeAndPay', paymentMethod is required.
    .test(
      'paymentMethod-required-charge',
      'Payment method is required',
      function(value) {
        const { status, paymentMethod } = value
        if (status === 'chargeAndPay' && !paymentMethod) {
          return this.createError({
            path: 'paymentMethod',
            message: 'Payment method is required',
          })
        }
        return true
      }
    )
    // Test 4: For status 'chargeAndPay' with credit_card or ach, paymentMethodId is required.
    .test(
      'paymentMethodId-required-charge',
      'Payment method is required',
      function(value) {
        const { status, paymentMethod, paymentMethodId } = value
        if (
          status === 'chargeAndPay' &&
          (paymentMethod === 'credit_card' || paymentMethod === 'ach') &&
          !paymentMethodId
        ) {
          return this.createError({
            path: 'paymentMethodId',
            message: 'Payment method is required',
          })
        }
        return true
      }
    )
    // Test 5: amountTargeted must be ≤ invoice.dueAmount (passed in context).
    .test(
      'amount-less-than-invoice',
      'Amount must be less than or equal to the invoice amount',
      function(value) {
        const { amountTargeted } = value
        const invoice = this.options.context?.invoice
        if (
          amountTargeted !== undefined &&
          invoice &&
          amountTargeted > invoice.dueAmount
        ) {
          return this.createError({
            path: 'amountTargeted',
            message: 'Amount must be less than or equal to the invoice amount',
          })
        }
        return true
      }
    )
    // Test 6: For status 'chargeAndPay' with paymentMethod 'check', additionalInfo is required.
    .test(
      'additionalInfo-required-check',
      'Additional payment info is required for this payment method',
      function(value) {
        const { status, paymentMethod, additionalInfo } = value
        if (
          status === 'chargeAndPay' &&
          paymentMethod === 'check' &&
          !additionalInfo
        ) {
          return this.createError({
            path: 'additionalInfo',
            message:
              'Additional payment info is required for this payment method',
          })
        }
        return true
      }
    )

  const {
    control,
    register,
    setError,
    setValue,
    formState: { errors, isSubmitting, isSubmitSuccessful },
    handleSubmit: handleSubmitForm,
    watch,
  } = useForm({
    defaultValues: {
      paymentMethodId,
      status: 'charge',
      paymentMethod: paymentMethod(),
      amountTargeted: type === 'charge' ? invoice.dueAmount : undefined,
    },
    resolver: yupResolver(schema, { context: { invoice } }),
  })
  const watchStatus = watch('status')
  const watchPaymentMethod = watch('paymentMethod')

  const onSubmit = async data => {
    try {
      if (type === 'change') {
        await changePaymentMethod({
          invoiceId: invoice.id,
          paymentMethodId: data.paymentMethodId,
          isDefaultMethod: data.status === 'changeAll',
        })
      } else {
        const isStripe =
          data.paymentMethod === 'credit_card' || data.paymentMethod === 'ach'
        await chargeInvoice({
          invoiceId: invoice.id,
          paymentData: {
            paymentMethod: data.paymentMethod,
            amountTargeted: data.amountTargeted,
            paymentMethodId: isStripe ? data.paymentMethodId : null,
            gateway: isStripe ? 'stripe' : 'on_site',
            additionalInfo: data.additionalInfo,
          },
        })
      }
    } catch (e) {
      setError('root.changePaymentMethod', {
        type: 'manual',
        message: e.message,
      })
    }
  }

  const handleSubmit = e => {
    e.preventDefault()
    const action = e.nativeEvent.submitter.name

    setValue('status', action)

    handleSubmitForm(data => onSubmit({ ...data, status: action }))()
  }

  useEffect(() => {
    if (isSubmitSuccessful) {
      onClose()
      const message =
        type === 'charge'
          ? 'Invoice paid successfully'
          : 'Payment method changed successfully'
      onOpenSnackbar(message)
    }
  }, [isSubmitSuccessful, onClose, onOpenSnackbar, type])

  const paymentMethodOptions = [
    { label: 'Cash', value: 'cash' },
    { label: 'Check', value: 'check' },
  ]

  if (hasPaymentMethods) {
    paymentMethodOptions.unshift({
      label: 'Credit Card',
      value: 'credit_card',
    })
  }

  if (hasBankAccounts) {
    paymentMethodOptions.push({
      label: 'ACH',
      value: 'ach',
    })
  }

  return (
    <FormControl component="form" onSubmit={handleSubmit} variant="filled">
      <FormErrorMessage errors={errors} name="root.changePaymentMethod" />

      {type === 'charge' && (
        <Box display={'flex'} gap={1}>
          <Typography fontSize="14px">Due:</Typography>
          <Typography bold fontSize="14px">
            ${toCurrency(invoice.amountPayable)}
          </Typography>
        </Box>
      )}

      <Divider />

      {type === 'charge' && (
        <>
          <CurrencyInputField
            label={'Amount'}
            name="amountTargeted"
            placeholder="$0.00"
            prefix="$"
            error={errors?.amountTargeted?.message}
            control={control}
          />

          <FormControl variant="standard">
            <Label>Payment Method</Label>

            <ControlRadioField
              id="paymentMethod"
              options={paymentMethodOptions}
              color="secondary"
              column
              property="value"
              name="paymentMethod"
              control={control}
            />
          </FormControl>
        </>
      )}

      <FormControl variant="standard">
        {type === 'charge' && watchPaymentMethod === 'credit_card' ? (
          <Label>Select a credit card</Label>
        ) : type === 'charge' && watchPaymentMethod === 'check' ? (
          <Label>Add check payment additional info</Label>
        ) : null}

        {watchPaymentMethod === 'credit_card' || type === 'change' ? (
          <SelectPaymentCardField
            control={control}
            name="paymentMethodId"
            paymentMethods={creditCards}
            error={errors?.paymentMethodId}
          />
        ) : null}

        {watchPaymentMethod === 'ach' || type === 'change' ? (
          <SelectPaymentAccountField
            control={control}
            name="paymentMethodId"
            paymentMethods={bankAccounts}
            error={errors?.paymentMethodId}
          />
        ) : null}

        {watchPaymentMethod === 'check' ? (
          <InputField
            {...register('additionalInfo')}
            error={errors?.additionalInfo?.message}
            fullWidth
            multiline
            sx={{ padding: '4px 0 5px; !important' }}
            label="Additional info"
            autoFocus
            placeholder="Write a payment additional info here..."
          />
        ) : null}
      </FormControl>

      <Grid
        display={'flex'}
        gap={1}
        flexWrap={isInstallPayment ? 'wrap' : 'nowrap'}
        justifyContent={'flex-end'}
      >
        {type === 'charge' && (
          <ActionButton
            variant="contained"
            color="secondary"
            type="submit"
            name="chargeAndPay"
            disabled={isSubmitting}
            loading={watchStatus === 'chargeAndPay' && isSubmitting}
          >
            {watchPaymentMethod === 'cash' || watchPaymentMethod === 'check'
              ? 'Mark as paid'
              : 'Charge Now'}
          </ActionButton>
        )}

        {type === 'change' && (
          <ActionButton
            variant="contained"
            color="secondary"
            type="submit"
            name="change"
            fullWidth={isInstallPayment}
            disabled={isSubmitting}
            loading={watchStatus === 'change' && isSubmitting}
          >
            {isInstallPayment
              ? 'Update only this Payment'
              : 'Change Payment Method'}
          </ActionButton>
        )}

        {type === 'change' && isInstallPayment ? (
          <ActionButton
            variant="contained"
            color="primary"
            type="submit"
            name="changeAll"
            fullWidth
            disabled={isSubmitting}
            loading={watchStatus === 'changeAll' && isSubmitting}
          >
            Update entire Payment Plan
          </ActionButton>
        ) : null}
      </Grid>
    </FormControl>
  )
}

export default ChangePaymentMethodForm
